import {addDocumentEventListener, getDocumentPropertyWithPrefix, removeDocumentEventListener} from './documentUtils';
import {globalScope} from './globalScope';
import {logger} from './Logger';
import {isValidString} from './stringUtils';
import {isNullish} from './Utils';

export class ViewportTracker {
  private onIntersectionChanged: () => void;
  private observer?: IntersectionObserver;
  private element: HTMLElement;
  private inViewport?: boolean = undefined;
  private threshold: number;
  private hiddenPropertyName: string | undefined;
  private visibilityChange?: string;

  constructor(element: HTMLElement, onIntersectionChanged: () => void, threshold = 1.0) {
    this.threshold = threshold;
    this.onIntersectionChanged = onIntersectionChanged;
    this.element = element;

    try {
      const options: IntersectionObserverInit = {
        root: null,
        rootMargin: '0px',
        threshold,
      };
      this.observer = new IntersectionObserver((entries, observer) => this.handleIntersect(entries, observer), options);
      this.observer.observe(element);
    } catch (_error) {
      logger.log("Couldn't create instance of IntersectionObserver. timeInViewport will always be reported as 100%.");
    }

    this.hiddenPropertyName = getDocumentPropertyWithPrefix('hidden');
    if (this.hiddenPropertyName) {
      this.visibilityChange = this.hiddenPropertyName.replace('hidden', '') + 'visibilitychange';
      addDocumentEventListener(
        this.visibilityChange,
        (this.handleVisibilityChange = this.handleVisibilityChange.bind(this)),
        false,
      );
    }
  }

  isHidden() {
    if (isNullish(globalScope.document) || !isValidString(this.hiddenPropertyName)) {
      return false;
    }

    return globalScope.document[this.hiddenPropertyName] === true;
  }

  isInViewport(): boolean {
    return !this.isHidden() && (isNullish(this.inViewport) ? true : this.inViewport);
  }

  dispose() {
    if (this.observer) {
      this.observer.unobserve(this.element);
      this.observer.disconnect();
    }
    if (this.visibilityChange) {
      removeDocumentEventListener(this.visibilityChange, this.handleVisibilityChange, false);
    }
  }

  private handleVisibilityChange() {
    this.onIntersectionChanged();
  }

  private handleIntersect(entries: IntersectionObserverEntry[], _observer: IntersectionObserver) {
    entries.forEach((i) => {
      if (i.target === this.element) {
        this.inViewport = !(i.intersectionRatio < this.threshold);
      }
    });
    this.onIntersectionChanged();
  }
}
